import { chunk, filter, forEach, get, head, map, size, take } from "lodash";

import { ItemList } from "../orm";
import moment from "moment";
import { photographList } from "./photograph";
import { postBlob } from "../lib/http";
import { sentryBreadcrumb } from "../utils/sentry";
import { upload_to_aws_directly } from "../lib/image";
import { RobustPhotographUploader } from "./robust_file_uploader";
import { BulkFileUploader } from "./bulk_file_uploader";
import { handleSubmitResult } from "./form";

const MAP_PLACEHOLDER_IMAGE_URL = null;
const PROCESSING_BATCH_SIZE = 10;
export const DEFAULT_NUM_ITEMS_PER_GALLERY_PAGE = 20;

const logger = {
  log: (...args) => console.log("GalleryList", ...args),
  error: (...args) => console.error("GalleryList", ...args),
};

const MIN_UPLOAD_SIZE = 20480;
const MAX_UPLOAD_SIZE = 5242880;

export class GalleryList extends ItemList {
  getEntityKey() {
    return "photographer/gallery";
  }

  invalidateGalleryAndPhotographs(gallery_id) {
    return (dispatch, getState) => {
      const gallery = this.getObject(gallery_id);
      forEach(gallery.photographs, (photograph) => {
        dispatch(photographList.invalidateObject(photograph.id));
        dispatch(photographList.ensureObjectLoaded(photograph.id));
      });
      dispatch(this.invalidateObject(gallery_id));
      dispatch(this.ensureObjectLoaded(gallery_id));
    };
  }

  getGalleryAvatarUrl(gallery) {
    const photograph_id =
      gallery.map_photograph || get(head(gallery.photographs), "id");
    if (!photograph_id) {
      return MAP_PLACEHOLDER_IMAGE_URL;
    }
    const photograph = head(
      filter(gallery.photographs, (x) => x.id === photograph_id),
    );
    if (!photograph) {
      return MAP_PLACEHOLDER_IMAGE_URL;
    }
    const download_url = get(
      photograph,
      ["avatar_size_file_info", "download_url"],
      MAP_PLACEHOLDER_IMAGE_URL,
    );
    return download_url;
  }

  async processFileUpload({
    file,
    gallery_id,
    gallery,
    gallery_photographs,
    country_code,
    onFileProcessed,
    onFileUploadingProgress,
  }) {
    const is_duplicate =
      size(
        map(
          filter(
            gallery_photographs,
            (photograph) => photograph.photo_name === get(file, "name"),
          ),
        ),
      ) > 0;

    if (is_duplicate) {
      onFileUploadingProgress(file, "duplicate");
      onFileProcessed(file, null);
      return;
    }

    onFileUploadingProgress(file, "scaling");
    // const scaledFile = await scaleImageFile(file, highResOptions)
    const scaledFile = file;
    onFileUploadingProgress(file, "uploading");

    let awsResult;
    try {
      awsResult = await upload_to_aws_directly(scaledFile, country_code);
    } catch (error) {
      onFileProcessed(file, error);
      return;
    }

    const { s3_filepath, aws_file_url } = awsResult;

    // try {
    //   // validate S3 size matches uploaded size
    //   const { s3_bytes } = await aws_file_info(aws_file_url);

    //   // logger.log('s3_bytes', s3_bytes, 'original_size', file.size);

    //   // check S3 size matches browser size (within 1%)
    //   // this is on top of the earlier min/max size check pre upload
    //   if (s3_bytes < file.size * 0.99) {
    //     onFileProcessed(file, {
    //       reason: "s3_size_comparison_failed",
    //     });
    //     return;
    //   }
    // } catch (e) {
    //   onFileProcessed(file, {
    //     reason: "s3_size_comparison_api_error",
    //   });
    //   return;
    // }

    const blobData = new window.FormData();
    blobData.append("aws_path", s3_filepath);
    blobData.append("filename", scaledFile.name);
    blobData.append("gallery", gallery_id);
    blobData.append("content_type", scaledFile.type);
    blobData.append("country_code", country_code);
    blobData.append(
      "photograph_taken_at",
      moment(file.lastModifiedDate).format(),
    );

    try {
      const [json, response, isSuccessful] = await postBlob(
        "photographer/photograph",
        blobData,
      );
      if (isSuccessful) {
        onFileProcessed(file, null);
      } else {
        onFileProcessed(file, {
          reason: "photograph_api_failed",
          error: response,
        });
      }
    } catch (error) {
      onFileProcessed(file, { reason: "photograph_api_error", error });
    }
  }

  savePhotos(props) {
    const {
      files,
      gallery_id,
      gallery,
      gallery_photographs,
      country_code,
      on_file_upload_progress: onFileUploadingProgress,
    } = props;

    const successes = [];
    const failures = [];

    //logger.log("savePhotos", props);

    const unacceptedFiles = files.filter(
      (file) => file.size < MIN_UPLOAD_SIZE || file.size > MAX_UPLOAD_SIZE,
    );

    if (unacceptedFiles.length > 0) {
      return Promise.resolve({
        successes: [],
        failures: unacceptedFiles,
        reason: "unacceptedfilesize",
      });
    }

    const promise = new Promise(async (resolve, reject) => {
      map(files, (file) => {
        onFileUploadingProgress(file, "pending");
      });

      const onFileProcessed = (file, errorMessage) => {
        if (!errorMessage) {
          successes.push(file);
          onFileUploadingProgress(file, "success");
          console.log(
            `photoupload=success path=${file?.path} size=${file?.size}`,
          );
          // logger.log("DONE", file, errorMessage);
        } else {
          failures.push(file);
          onFileUploadingProgress(file, "failed", errorMessage);
          console.error(
            `photoupload=failed path=${file?.path} size=${file?.size} error=${errorMessage?.reason} msg=${errorMessage?.error?.message}`,
          );
          logger.error("FAILED", file, errorMessage);
          sentryBreadcrumb({
            category: "photoupload",
            level: "error",
            message: errorMessage?.reason,
            data: { path: file?.path, size: file?.size, msg: errorMessage?.error?.message },
          });
        }

        if (size(successes) + size(failures) === size(files)) {
          resolve({ successes, failures });
        }
      };

      const fileBatches = chunk(files, PROCESSING_BATCH_SIZE);

      let batchCursor = fileBatches.length;

      const _processFileUpload = this.processFileUpload;

      function processBatch() {
        if (batchCursor > 0) {
          batchCursor--;
        } else {
          return;
        }
        const fileBatch = fileBatches[batchCursor];
        // logger.log(`PROCESSING BATCH ${batchCursor}`, fileBatch);
        Promise.allSettled(
          fileBatch.map((file) =>
            _processFileUpload({
              file,
              gallery_id,
              gallery,
              gallery_photographs,
              country_code,
              onFileProcessed,
              onFileUploadingProgress,
            }),
          ),
        ).then(() => {
          processBatch();
        });
      }

      processBatch();
    });

    return promise;
  }

  savePhotosv2(props) {
    const {
      files,
      galleryPhotographs,
      galleryId,
      countryCode,
      on_file_upload_progress,
    } = props
    const robustPhotographUploader = new RobustPhotographUploader(
      files, galleryPhotographs, galleryId, countryCode, on_file_upload_progress
    )

    return robustPhotographUploader.uploadFiles()
  }

  savePhotosv3({
    photos,
    credentials,
    gallery,
    fileUploadsInProgress,
    setFileUploadsInProgress,
    setBulkUploadProgress,
    setBulkUploadStatus,
    setBulkUploadInProgress,
    onUploadComplete,
  }) {
    return async (dispatch, getState) => {
      const onFileProgress = (file, status, info) => {
        fileUploadsInProgress[file.path] = { status, ...info }
        setFileUploadsInProgress({ ...fileUploadsInProgress })
        if (status === 'success' && info?.uploaded !== undefined) {
          setBulkUploadProgress({ uploaded: info.uploaded, total: info.total })
        }
      }

      const onBatchComplete = ({ uploadedCount, totalFiles }) => {
        setBulkUploadProgress({ uploaded: uploadedCount, total: totalFiles })
      }

      const onRegistering = () => {
        setBulkUploadStatus('registering')
      }

      const onError = (error) => {
        setBulkUploadInProgress(false)
        setBulkUploadProgress({ uploaded: 0, total: 0 })
        setBulkUploadStatus(null)
      }

      const bulkPhotographUploader = new BulkFileUploader({
        files: photos,
        credentials: credentials,
        gallery: gallery,
        onFileProgress: onFileProgress,
        onBatchComplete: onBatchComplete,
        onRegistering: onRegistering,
        onComplete: onUploadComplete,
        onError: onError,
      })

      bulkPhotographUploader.uploadFiles()
    }
  }

  saveGallery(values, selectedGallery, formProps, on_ok) {
    return (dispatch, getState) => {
      const existingGallery = Boolean(selectedGallery && selectedGallery.id)
      if (existingGallery) {
        dispatch(
          this.saveObject({
            ...selectedGallery,
            ...values,
            id: selectedGallery.id,
          })
        ).then((res) => handleSubmitResult({ res, formProps, on_ok}))
      } else {
        dispatch(
          this.saveNewObject(values)
        ).then((res) => handleSubmitResult({ res, formProps, on_ok}))
      }
    }
  }

  getRecentGalleries() {
    return take(
      filter(this.getVisibleObjects(), (gallery) => gallery.gallery_type !== 'photo_art'), 3
    )
  }
}

export class MobileGalleriesList extends GalleryList {}

export const mobileGalleriesList = new MobileGalleriesList("mobileGalleries")

export class GalleryUploadCredentialsList extends ItemList {
  getEntityKey() {
    return "photographer/gallery/upload_credentials";
  }

  getFor({ galleryRef }) {
    return this.saveNewObject({
      gallery_ref: galleryRef,
    })
  }
}

export class GalleryDetailedList extends ItemList {
  getEntityKey() {
    return "photographer/gallery/get_detailed_object";
  }

  get({ galleryRef }) {
    return this.saveNewObject({
      gallery_ref: galleryRef,
    })
  }
}

class SetMapPhotographList extends ItemList {
  getEntityKey() {
    return "photographer/gallery/set_map_photograph";
  }

  set({ galleryRef, photographId }) {
    return this.saveNewObject({
      gallery_ref: galleryRef,
      photograph_id: photographId,
    })
  }
}

class ReplicateGalleryList extends ItemList {
  getEntityKey() {
    return "photographer/gallery/replicate_gallery";
  }

  replicate({ galleryRef, sessionName }) {
    return this.saveNewObject({
      gallery_ref: galleryRef,
      session_name: sessionName,
    })
  }
}

class SetGalleryPrivacyLevelList extends ItemList {
  getEntityKey() {
    return "photographer/gallery/set_privacy_level"
  }

  setPrivacyLevel({ galleryRef, privacyLevel }) {
    return this.saveNewObject({
      gallery_ref: galleryRef,
      privacy_level: privacyLevel,
    })
  }
}

export const galleryList = new GalleryList("gallery_default");
export const galleryUploadCredentialsList = new GalleryUploadCredentialsList("gallery_upload_credentials_default");
export const galleryDetailedList = new GalleryDetailedList("gallery_detailed_default");
export const setMapPhotographList = new SetMapPhotographList("set_map_photograph_default");
export const replicateGalleryList = new ReplicateGalleryList("replicate_gallery_default");
export const setGalleryPrivacyLevelList = new SetGalleryPrivacyLevelList("set_gallery_privacy_level_default");
